import React, { useState, useEffect } from 'react';
import { Search, Phone, RectangleEllipsis, Trash2, ShoppingCart, CreditCard, Package, Plus, Minus, X, Printer } from 'lucide-react';

// Helpers
import { formatCurrency } from '../Helpers';

import { InputField } from './CommonComponents';
import Toast from './Toast';
import { saleCompleteFeedback, addToCartFeedback, deleteFeedback, undoFeedback } from '../utils/feedback';
import { generateReceiptPDF } from '../utils/receiptPDF';

const PointOfSale = ({ inventory, onFinalizeSale, shopName = 'StoreFlow' }) => {
    const [cart, setCart] = useState([]);
    const [customerForm, setCustomerForm] = useState({
        name: '',
        phone: ''
    });

    const handlePhoneChange = (e) => {
        // Allow only digits, +, -, spaces, and parentheses (for country codes)
        const value = e.target.value.replace(/[^0-9+\-() ]/g, '');
        setCustomerForm({ ...customerForm, phone: value });
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [showSaleCompletedModal, setShowSaleCompletedModal] = useState(false);
    const [showMobileCart, setShowMobileCart] = useState(false);
    const [mobileCartAnimating, setMobileCartAnimating] = useState(false);
    const [toast, setToast] = useState(null);
    const [lastCompletedSale, setLastCompletedSale] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('cash');

    const openMobileCart = () => {
        setShowMobileCart(true);
        setTimeout(() => setMobileCartAnimating(true), 10);
    };

    const closeMobileCart = () => {
        setMobileCartAnimating(false);
        setTimeout(() => setShowMobileCart(false), 300);
    };

    const availableInventory = inventory.filter(item =>
        item.quantity > 0 && item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const addToCart = (item) => {
        const existingItem = cart.find(cartItem => cartItem.id === item.id);
        if (existingItem) {
            if (existingItem.quantity < item.quantity) {
                setCart(cart.map(cartItem =>
                    cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
                ));
                addToCartFeedback();
            } else {
                console.warn("Cannot add more than available stock.");
            }
        } else {
            setCart([...cart, { ...item, quantity: 1 }]);
            addToCartFeedback();
        }
    };

    const updateCartQuantity = (itemId, newQuantity) => {
        const stockItem = inventory.find(item => item.id === itemId);
        const quantity = Math.max(0, Math.min(stockItem.quantity, parseInt(newQuantity, 10) || 0));

        if (quantity === 0) {
            setCart(cart.filter(item => item.id !== itemId));
        } else {
            setCart(cart.map(item =>
                item.id === itemId ? { ...item, quantity } : item
            ));
        }
    };

    const updateCartPrice = (itemId, newPrice) => {
        const price = parseFloat(newPrice);
        if (isNaN(price) || price < 0) return;
        setCart(cart.map(item =>
            item.id === itemId ? { ...item, sellingPrice: price, priceOverridden: true } : item
        ));
    };

    const resetCartPrice = (itemId) => {
        const originalItem = inventory.find(item => item.id === itemId);
        if (originalItem) {
            setCart(cart.map(item =>
                item.id === itemId ? { ...item, sellingPrice: originalItem.sellingPrice, priceOverridden: false } : item
            ));
        }
    };

    const incrementQuantity = (itemId) => {
        const cartItem = cart.find(item => item.id === itemId);
        const stockItem = inventory.find(item => item.id === itemId);
        if (cartItem && cartItem.quantity < stockItem.quantity) {
            updateCartQuantity(itemId, cartItem.quantity + 1);
        }
    };

    const decrementQuantity = (itemId) => {
        const cartItem = cart.find(item => item.id === itemId);
        if (cartItem && cartItem.quantity > 1) {
            updateCartQuantity(itemId, cartItem.quantity - 1);
        }
    };

    const removeFromCart = (itemId) => {
        const removedItem = cart.find(item => item.id === itemId);
        const previousCart = [...cart];
        setCart(cart.filter(item => item.id !== itemId));
        deleteFeedback();

        setToast({
            id: Date.now(),
            message: `${removedItem?.itemName} removed from cart`,
            type: 'delete',
            duration: 4000,
            undoAction: () => {
                setCart(previousCart);
                undoFeedback();
            }
        });
    };

    const totalAmount = cart.reduce((sum, item) => sum + item.sellingPrice * item.quantity, 0);

    const handleFinalize = () => {
        const previousCart = [...cart];
        const previousCustomerForm = { ...customerForm };

        // Build sale record for bill generation
        const saleRecord = {
            id: Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
            saleDate: { seconds: Math.floor(Date.now() / 1000) },
            customer: {
                name: customerForm.name,
                phone: customerForm.phone
            },
            items: cart.map(item => ({
                itemId: item.id,
                itemName: item.itemName,
                sellingPrice: item.sellingPrice,
                quantity: item.quantity
            })),
            totalAmount: totalAmount,
            paymentMethod: paymentMethod
        };

        setLastCompletedSale(saleRecord);
        onFinalizeSale(cart, customerForm, paymentMethod);
        setCart([]);
        setSearchTerm('');
        setPaymentMethod('cash');
        setShowSaleCompletedModal(true);

        // Sound and haptic feedback on sale completion
        saleCompleteFeedback();

        // Show toast with undo option
        setToast({
            id: Date.now(),
            message: `Sale completed — ${formatCurrency(totalAmount)}`,
            type: 'success',
            duration: 6000,
            undoAction: () => {
                // Restore cart (note: the Firestore write already happened,
                // but restoring the UI lets the user re-check)
                setCart(previousCart);
                setCustomerForm(previousCustomerForm);
                undoFeedback();
            }
        });
    };

    useEffect(() => {
        let timerId;

        if (showSaleCompletedModal) {
            timerId = setTimeout(() => {
                console.log('This message appears after 6 seconds.');
                setShowSaleCompletedModal(false);
            }, 6000); // 6s - matches toast duration
        }

        return () => clearTimeout(timerId);
    }, [showSaleCompletedModal]);


    const handleSaleCompletionConfirm = async () => {
        setShowSaleCompletedModal(false);
    };

    const handleDownloadBill = () => {
        if (lastCompletedSale) {
            const { pdf, filename } = generateReceiptPDF(lastCompletedSale, shopName);
            pdf.save(filename);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-4 sm:p-8 text-white">
                <div className="flex items-center space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-3 sm:p-4">
                        <ShoppingCart className="h-8 w-8 sm:h-12 sm:w-12" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold">Point of Sale</h1>
                        <p className="text-purple-100 mt-1 text-sm sm:text-base">Process sales and manage transactions</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 pb-20 lg:pb-0">
                {/* Product Selection */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                            <Package className="h-5 w-5 mr-2 text-purple-600" />
                            Available Products
                        </h2>
                    </div>
                    <div className="p-6">
                        <div className="mb-6 relative">
                            <input
                                type="text"
                                placeholder="Search for items to sell..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full p-3 sm:p-4 pl-10 sm:pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-sm sm:text-base"
                            />
                            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        </div>

                        {availableInventory.length === 0 ? (
                            <div className="text-center py-12">
                                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No products available</h3>
                                <p className="text-gray-500">
                                    {searchTerm ? 'Try adjusting your search terms' : 'Add inventory items to start selling'}
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 max-h-[60vh] overflow-y-auto border border-double rounded-lg p-2">
                                {availableInventory.map(item => (
                                    <button
                                        key={item.id}
                                        onClick={() => addToCart(item)}
                                        className="border border-gray-200 rounded-lg p-3 sm:p-4 text-center hover:bg-purple-50 hover:border-purple-300 hover:shadow-md transition-all duration-200 group"
                                    >
                                        <div className="bg-gray-100 group-hover:bg-purple-100 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3 transition-colors">
                                            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 group-hover:text-purple-600 mx-auto transition-colors" />
                                        </div>
                                        <p className="font-semibold text-gray-800 mb-1 text-sm sm:text-base truncate">{item.itemName}</p>
                                        <p className="text-base sm:text-lg font-bold text-purple-600 mb-1">{formatCurrency(item.sellingPrice)}</p>
                                        <p className="text-xs text-gray-500">Stock: {item.quantity}</p>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Shopping Cart - Desktop */}
                <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 lg:sticky lg:top-4">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                            <ShoppingCart className="h-5 w-5 mr-2 text-purple-600" />
                            Current Sale ({cart.length} items)
                        </h2>
                    </div>
                    <div className="p-6">
                        <div className="space-y-4 max-h-[45vh] overflow-y-auto">
                            {cart.length === 0 ? (
                                <div className="text-center py-8">
                                    <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">Your cart is empty</p>
                                    <p className="text-sm text-gray-400 mt-1">Add items to start a sale</p>
                                </div>
                            ) : (
                                cart.map(item => (
                                    <div key={item.id} className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-900">{item.itemName}</p>
                                                <div className="flex items-center mt-1">
                                                    <span className="text-sm text-gray-500 mr-1">₹</span>
                                                    <input
                                                        type="number"
                                                        value={item.sellingPrice}
                                                        onChange={(e) => updateCartPrice(item.id, e.target.value)}
                                                        className={`w-20 py-0.5 text-sm bg-transparent border-b outline-none focus:border-purple-500 ${item.priceOverridden
                                                            ? 'border-orange-400 text-orange-700 font-medium'
                                                            : 'border-transparent text-gray-600 hover:border-gray-300'
                                                            }`}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    {item.priceOverridden && (
                                                        <button
                                                            onClick={() => resetCartPrice(item.id)}
                                                            className="ml-2 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-600 rounded hover:bg-orange-200 transition-colors"
                                                        >
                                                            ↺
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors touch-target"
                                                title="Remove from cart"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => decrementQuantity(item.id)}
                                                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors touch-target"
                                                    disabled={item.quantity <= 1}
                                                >
                                                    <Minus size={16} />
                                                </button>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateCartQuantity(item.id, e.target.value)}
                                                    className="w-16 p-2 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                                    min="1"
                                                    max={inventory.find(i => i.id === item.id)?.quantity}
                                                />
                                                <button
                                                    onClick={() => incrementQuantity(item.id)}
                                                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors touch-target"
                                                    disabled={item.quantity >= inventory.find(i => i.id === item.id)?.quantity}
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                            <p className="font-bold text-gray-900">
                                                {formatCurrency(item.sellingPrice * item.quantity)}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Total and Checkout */}
                        <div className="mt-6 border-t border-gray-200 pt-6">
                            <div className="bg-purple-50 rounded-lg p-4 mb-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-lg font-semibold text-gray-700">Total Amount:</span>
                                    <span className="text-2xl font-bold text-purple-600">{formatCurrency(totalAmount)}</span>
                                </div>
                            </div>
                            <InputField
                                label="Customer Name (optional)"
                                name="customerName"
                                value={customerForm.name}
                                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                                icon={RectangleEllipsis}
                                className="mb-4"
                                type="text"
                                placeholder="Enter customer name"
                                required
                            />
                            <InputField
                                label="Customer Phone No (optional)"
                                name="customerPhone"
                                value={customerForm.phone}
                                onChange={handlePhoneChange}
                                icon={Phone}
                                placeholder="9876543210"
                                maxLength={10}
                                pattern="[0-9]*"
                                type="tel"
                            />
                            {/* Payment Method Tabs */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('cash')}
                                        className={`flex-1 py-2.5 text-sm font-medium transition-colors ${paymentMethod === 'cash'
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        💵 Cash
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('upi')}
                                        className={`flex-1 py-2.5 text-sm font-medium border-l border-gray-300 transition-colors ${paymentMethod === 'upi'
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        📱 UPI
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={handleFinalize}
                                disabled={cart.length === 0}
                                className="mt-4 w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-4 rounded-lg text-lg font-semibold shadow-sm transition-colors flex items-center justify-center space-x-2"
                            >
                                <CreditCard size={20} />
                                <span>Complete Sale</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Shopping Cart - Fixed Bottom */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
                <div className="p-4">
                    {cart.length === 0 ? (
                        <div className="text-center py-2">
                            <p className="text-gray-500 text-sm">Your cart is empty</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* Cart Summary */}
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => openMobileCart()}
                                    className="flex items-center space-x-2 flex-1"
                                >
                                    <ShoppingCart className="h-5 w-5 text-purple-600" />
                                    <span className="font-semibold text-gray-900">{cart.length} items</span>
                                </button>
                                <div className="bg-purple-50 rounded-lg px-3 py-1">
                                    <span className="text-lg font-bold text-purple-600">{formatCurrency(totalAmount)}</span>
                                </div>
                            </div>

                            {/* Checkout Button - Opens cart modal with customer details */}
                            <button
                                onClick={() => openMobileCart()}
                                disabled={cart.length === 0}
                                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                            >
                                <CreditCard size={18} />
                                <span>Checkout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Cart Modal */}
            {showMobileCart && (
                <div
                    className={`lg:hidden fixed inset-0 z-50 flex items-end transition-colors !mt-0 duration-300 ${mobileCartAnimating ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0'}`}
                    onClick={(e) => { if (e.target === e.currentTarget) closeMobileCart(); }}
                >
                    <div className={`bg-white w-full max-h-[80vh] rounded-t-xl overflow-hidden transform transition-transform duration-300 ease-out ${mobileCartAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Current Sale</h3>
                            <button
                                onClick={() => closeMobileCart()}
                                className="p-2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            <div className="space-y-4">
                                {cart.map(item => (
                                    <div key={item.id} className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-900">{item.itemName}</p>
                                                <div className="flex items-center mt-1">
                                                    <span className="text-sm text-gray-500 mr-1">₹</span>
                                                    <input
                                                        type="number"
                                                        value={item.sellingPrice}
                                                        onChange={(e) => updateCartPrice(item.id, e.target.value)}
                                                        className={`w-20 py-0.5 text-sm bg-transparent border-b outline-none focus:border-purple-500 ${item.priceOverridden
                                                            ? 'border-orange-400 text-orange-700 font-medium'
                                                            : 'border-transparent text-gray-600 hover:border-gray-300'
                                                            }`}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    {item.priceOverridden && (
                                                        <button
                                                            onClick={() => resetCartPrice(item.id)}
                                                            className="ml-2 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-600 rounded hover:bg-orange-200 transition-colors"
                                                        >
                                                            ↺
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => decrementQuantity(item.id)}
                                                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                                                    disabled={item.quantity <= 1}
                                                >
                                                    <Minus size={16} />
                                                </button>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateCartQuantity(item.id, e.target.value)}
                                                    className="w-16 p-2 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                                    min="1"
                                                    max={inventory.find(i => i.id === item.id)?.quantity}
                                                />
                                                <button
                                                    onClick={() => incrementQuantity(item.id)}
                                                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                                                    disabled={item.quantity >= inventory.find(i => i.id === item.id)?.quantity}
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                            <p className="font-bold text-gray-900">
                                                {formatCurrency(item.sellingPrice * item.quantity)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-200">
                            <div className="bg-purple-50 rounded-lg p-4 mb-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-lg font-semibold text-gray-700">Total Amount:</span>
                                    <span className="text-2xl font-bold text-purple-600">{formatCurrency(totalAmount)}</span>
                                </div>
                            </div>
                            <InputField
                                label="Customer Name (optional)"
                                name="customerName"
                                value={customerForm.name}
                                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                                icon={RectangleEllipsis}
                                className="mb-4"
                                type="text"
                                placeholder="Enter customer name"
                            />
                            <InputField
                                label="Customer Phone No (optional)"
                                name="customerPhone"
                                value={customerForm.phone}
                                onChange={handlePhoneChange}
                                icon={Phone}
                                placeholder="+91 98765 43210"
                                maxLength={10}
                                pattern="[0-9]*"
                                type="tel"
                            />
                            {/* Payment Method Tabs */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('cash')}
                                        className={`flex-1 py-2.5 text-sm font-medium transition-colors ${paymentMethod === 'cash'
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        💵 Cash
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('upi')}
                                        className={`flex-1 py-2.5 text-sm font-medium border-l border-gray-300 transition-colors ${paymentMethod === 'upi'
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        📱 UPI
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    handleFinalize();
                                    closeMobileCart();
                                }}
                                disabled={cart.length === 0}
                                className="mt-4 w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-4 rounded-lg text-lg font-semibold shadow-sm transition-colors flex items-center justify-center space-x-2"
                            >
                                <CreditCard size={20} />
                                <span>Complete Sale</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sale Completion Modal */}
            {showSaleCompletedModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="p-6 sm:p-8 bg-white rounded-xl shadow-lg max-w-md w-full text-center">
                        <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <CreditCard className="h-8 w-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Sale Completed!</h2>
                        {lastCompletedSale && (
                            <p className="text-gray-500 mb-6">
                                Total: {formatCurrency(lastCompletedSale.totalAmount)}
                            </p>
                        )}
                        <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                            <button
                                onClick={handleDownloadBill}
                                className="flex items-center justify-center space-x-2 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-900 transition-colors"
                            >
                                <Printer className="h-4 w-4" />
                                <span>Download Bill</span>
                            </button>
                            <button
                                onClick={handleSaleCompletionConfirm}
                                className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast && (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    undoAction={toast.undoAction}
                    onDismiss={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default PointOfSale;